import { Box, Typography } from '@mui/material'
import { emptyIndicator } from '@planning/constants'
import { IGeneralCargoUploadOrderDto } from '@planning/pages/Order/stores/OrderListUploadViewStoreV2'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { FC } from 'react'

interface IProps {
  order: IGeneralCargoUploadOrderDto
  index: number
  showRailFields?: boolean
}

export const GeneralCargoUploadOrderCard: FC<IProps> = ({ order, index, showRailFields }) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()

  const renderInfo = (key: string, label: string, value?: string | number) => (
    <Box
      key={key}
      mr={theme.customSpacing.l}
      sx={{
        whiteSpace: 'nowrap',
        gap: '0.5rem',
      }}
    >
      <Typography variant='caption' color={theme.palette.grey[600]}>
        {label}
      </Typography>
      <Typography variant='subtitle2'>{value || value === 0 ? value : emptyIndicator}</Typography>
    </Box>
  )

  const grossWeight =
    order.grossWeight || order.grossWeight === 0
      ? [order.grossWeight, order.weightUnit].filter(Boolean).join(' ')
      : undefined

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.customRadius.medium,
        m: '1rem 0',
      }}
    >
      <Box
        sx={{
          padding: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant='h4'>
            {order.referenceNumber || `${t('order', 'Order')} #${index + 1}`}
          </Typography>
          <Typography
            variant='subtitle2'
            color={theme.palette.grey[600]}
            sx={{ mt: theme.customSpacing.xs }}
          >
            {order.commodityName
              ? `${t('commodity', 'Commodity')}: ${order.commodityName}`
              : `${t('commodity', 'Commodity')} ID: ${order.commodityId}`}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: theme.palette.grey[200],
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: theme.customSpacing.l,
          padding: '1rem',
        }}
      >
        {renderInfo('lot-number', t('lotNumber', 'Lot number'), order.lotNumber)}
        {renderInfo(
          'planned-amount',
          t('plannedQuantity', 'Planned quantity'),
          order.plannedAmount,
        )}
        {renderInfo('gross-weight', t('totalGrossWeight', 'Total gross weight'), grossWeight)}
        {renderInfo('imo-classes', t('IMOClass', 'IMO Class'), order.imoClasses.join(', '))}
        {renderInfo(
          'ids',
          t('identifiableItems', 'ID'),
          order.identifiableItems.length ? order.identifiableItems.join(', ') : undefined,
        )}
        {showRailFields && (
          <>
            {renderInfo(
              'rail-track',
              t('railTrack', 'Rail track'),
              order.railTrackName ?? order.railTrackId,
            )}
            {renderInfo(
              'railcar',
              t('railCarNumber', 'Rail car number'),
              order.railcarNumber ?? order.railcarId,
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
